/**
 * Delivered sessions & payment facts — verified against cms-backend
 * src/sessions/{dto,sessions.controller,sessions.service}.ts at main 2c166d1
 * (B5.2 / ZOY-189; Product 2026-09-06 capacity + patient account; api-v1.0
 * additive).
 *
 * The delivered session is THE session-done signal: at most one EFFECTIVE
 * delivery per booking — a voided booking can be delivered again, the voided
 * row staying in history. payableAmount is snapshotted at delivery (package
 * slot, or the clinic catalogue for an over-plan / ad-hoc delivery —
 * `priceBasis`) and changes only through the ledgered over-plan amount
 * correction. Payment facts are PROJECTIONS of the patient account
 * (paymentsApi.ts): `paidAmount` and `paymentStatus` unpaid | partial | paid;
 * the session-level PATCH is a shortcut over that account. Corrections are
 * append-only void / reinstate with a mandatory reason. Reads: per patient
 * (unbounded, voided rows included), by id, and the clinic-wide query
 * GET /v1/delivered-sessions (ZOY-262; features/sessions/sessionQueries.ts)
 * whose envelope adds a full-filter `summary`.
 */

import { apiRequest } from '../api/http';
import type { BookingView, CreateBookingBody, LocationType } from './bookingsApi';

export const PAYMENT_METHODS = ['pos_card', 'online', 'cash', 'card_to_card'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
/** What a READ says: `partial` is a projection of the patient account (some, not all, applied). */
export const PAYMENT_STATUSES = ['unpaid', 'partial', 'paid'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];
/** Money still owed on the session — the work-queue meaning of "unpaid". */
export const OWING_STATUSES = ['unpaid', 'partial'] as const satisfies readonly PaymentStatus[];
/** What the session-level PATCH accepts: mark paid (records the remaining amount) or unpaid (reverses). */
export type PaymentFactsStatus = 'unpaid' | 'paid';
/** Where a delivery's amount came from (Product 2026-09-06). */
export const PRICE_BASES = ['package_slot', 'catalog'] as const;
export type PriceBasis = (typeof PRICE_BASES)[number];
export const CORRECTION_ACTIONS = ['void', 'reinstate'] as const;
export type CorrectionAction = (typeof CORRECTION_ACTIONS)[number];

export interface DeliveredSessionView {
  id: string;
  bookingId: string;
  patientId: string;
  serviceId: string;
  packageItemId: string | null;
  deliveredByUid: string;
  deliveredAt: string;
  locationType: LocationType | (string & {});
  courseIndex: number | null;
  /** CLINICAL — never rendered on a payment surface. */
  notes: string | null;
  /** CLINICAL — never rendered on a payment surface. */
  outcomeNotes: string | null;
  /** Fixed-2 string, snapshotted at delivery; movable only by the over-plan amount correction. */
  payableAmount: string;
  /** Fixed-2 string: what the patient account has applied to this charge. */
  paidAmount: string;
  paymentStatus: PaymentStatus;
  paymentMethod: PaymentMethod | null;
  paidAt: string | null;
  /** Package slot, or the clinic catalogue (over-plan and ad-hoc deliveries). */
  priceBasis: PriceBasis;
  /** Derived: a package delivery priced from the catalogue because the plan was already consumed. */
  overPlan?: boolean;
  overPlanReason: string | null;
  /**
   * Legacy only: a row voided while its payment was recorded BEFORE the
   * patient-account release. New voids release the money as credit instead.
   */
  financialAttention?: 'voided_paid' | null;
  effective: boolean;
  recordedBy: string;
  createdAt: string;
  service: { name: string };
  /**
   * ZOY-651: the staff names, resolved by the backend for anyone who may read
   * the session (no personnel access needed); `null` when Auth cannot answer.
   * Absent on reads made before the field existed.
   */
  deliveredByName?: string | null;
  recordedByName?: string | null;
  /**
   * ZOY-686: the shared Group Report of the occurrence this session's booking
   * participated in — on the by-id and per-patient reads only; null for a
   * single-patient session. CLINICAL — never rendered on a payment surface.
   */
  groupReport?: GroupReport | null;
}

export interface GroupReport {
  id: string;
  body: string;
  authoredBy: string;
  authoredAt: string;
}

/**
 * A row of GET /v1/delivered-sessions (ZOY-262): the session view plus the
 * context a table needs without per-row lookups. Notes stay CLINICAL and
 * are stripped before any payment surface renders the row.
 */
export interface DeliveredSessionListItem extends DeliveredSessionView {
  patient: { firstName: string; lastName: string };
  booking: { startsAt: string; endsAt: string; practitionerUid: string };
  /** ZOY-651: the anchoring booking's practitioner, by name. */
  practitionerName?: string | null;
}

/**
 * Full-filter operational summary (never the current page): sums of
 * snapshotted payable amounts grouped by the recorded payment status. NOT
 * collected / revenue / outstanding — the model has no ledger.
 */
export interface DeliveredSessionSummary {
  sessionCount: number;
  paidCount: number;
  unpaidCount: number;
  partialCount: number;
  /** Fixed-2 strings in the clinic currency. */
  paidPayableAmount: string;
  unpaidPayableAmount: string;
  totalPayableAmount: string;
  /** Σ paidAmount over the matching set. */
  paidAmountTotal: string;
  /** Σ (payableAmount − paidAmount) over the matching set — the real "still owed". */
  outstandingAmount: string;
  /** Legacy voided-but-paid rows (pre-account release) in the matching set. */
  voidedPaidCount: number;
}

export interface SessionCorrection {
  id: string;
  action: CorrectionAction;
  reason: string;
  correctedBy: string;
  /** ZOY-651: resolved by the backend; `null` when Auth cannot answer. */
  correctedByName?: string | null;
  createdAt: string;
}

export interface DeliveredSessionDetail extends DeliveredSessionView {
  corrections: SessionCorrection[];
  /** ZOY-487: finalized recordings on this record — travels with the read, so
   *  a list of records fires no per-row lookup. */
  recordingCount?: number;
}

export const SessionErrorCode = {
  NotFound: 'SESSION_NOT_FOUND',
  AlreadyDelivered: 'SESSION_ALREADY_DELIVERED',
  BookingNotActive: 'SESSION_BOOKING_NOT_ACTIVE',
  PractitionerNotFound: 'SESSION_PRACTITIONER_NOT_FOUND',
  PaymentInvalid: 'SESSION_PAYMENT_INVALID',
  CorrectionInvalidState: 'SESSION_CORRECTION_INVALID_STATE',
  /** Reinstating a voided row whose booking was delivered again meanwhile. */
  BookingRedelivered: 'SESSION_BOOKING_REDELIVERED',
  /** The amount correction is for an EFFECTIVE OVER-PLAN delivery only. */
  PayableAmountLocked: 'SESSION_PAYABLE_AMOUNT_LOCKED',
  /** Unpaid-ing here would reverse a payment that also settles other charges. */
  PaymentNotReversibleHere: 'SESSION_PAYMENT_NOT_REVERSIBLE_HERE',
  /**
   * ZOY-548: the booked service declares the assessment workflow and THIS
   * booking's own assessment is not signed off — completion is refused.
   */
  ClinicalWorkflowIncomplete: 'SESSION_CLINICAL_WORKFLOW_INCOMPLETE',
  /**
   * Product 2026-10-01 (422): a delivery with neither notes nor outcome is
   * refused — nothing is written. Group participations and assessment-
   * workflow sessions are exempt server-side as they are in the form.
   */
  ReportRequired: 'SESSION_REPORT_REQUIRED',
} as const;

/* ──────────────────── session execution lifecycle (ZOY-546/547) ─────────── */

/** The therapist's start act — one-shot, attributed; returns the fresh booking view. */
export const startSession = (bookingId: string) =>
  apiRequest<BookingView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/bookings/${bookingId}/start`,
    body: {},
  });

/**
 * ZOY-547: close a STARTED session that could not be clinically completed.
 * Terminal and honest: nothing payable, no package progress, never
 * deliverable afterwards. The reason is REQUIRED by product decision.
 */
export const closeSessionIncomplete = (bookingId: string, reason: string) =>
  apiRequest<BookingView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/bookings/${bookingId}/incomplete`,
    body: { reason },
  });

export interface PaymentFacts {
  status: PaymentFactsStatus;
  /** Required when paid; must be absent while unpaid. */
  method?: PaymentMethod;
}

export interface DeliverSessionBody {
  deliveredAt?: string;
  locationType?: LocationType;
  deliveredByUid?: string;
  notes?: string;
  outcomeNotes?: string;
  payment?: PaymentFacts;
  /** Kept only when the delivery turns out to be beyond the plan; never required. */
  overPlanReason?: string;
}

/** Information, never a failure: the delivery WAS recorded. */
export interface UpfrontShortfallWarning {
  code: 'PACKAGE_UPFRONT_SHORTFALL';
  packageId: string;
  packageTotal: string;
  earmarkedReceived: string;
  shortfall: string;
}
export type DeliveryWarning = UpfrontShortfallWarning;

/** The line's capacity AFTER a correction, as the backend computed it. */
export interface PackageCapacity {
  plannedSessions: number;
  deliveredSessions: number;
  scheduledSessions: number;
  remainingToSchedule: number;
  overScheduled: number;
  overDelivered: number;
}

export const deliverForBooking = (bookingId: string, body: DeliverSessionBody) =>
  apiRequest<DeliveredSessionView & { warnings: DeliveryWarning[] }>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/bookings/${bookingId}/delivered-session`,
    body,
  });

/** Ad-hoc same-day delivery: commits a booking through the scheduling path first. */
export const deliverAdHoc = (
  patientId: string,
  body: DeliverSessionBody & { booking: CreateBookingBody },
) =>
  apiRequest<DeliveredSessionView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/patients/${patientId}/delivered-sessions`,
    body,
  });

export const listPatientSessions = (patientId: string) =>
  apiRequest<{ items: DeliveredSessionView[] }>({
    base: 'clinic',
    path: `/v1/patients/${patientId}/delivered-sessions`,
  });

export const fetchSession = (id: string) =>
  apiRequest<DeliveredSessionDetail>({ base: 'clinic', path: `/v1/delivered-sessions/${id}` });

export const correctSession = (id: string, body: { action: CorrectionAction; reason: string }) =>
  apiRequest<DeliveredSessionView & { packageCapacity: PackageCapacity | null }>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/delivered-sessions/${id}/corrections`,
    body,
  });

export const recordSessionPayment = (id: string, body: PaymentFacts) =>
  apiRequest<DeliveredSessionView>({
    base: 'clinic',
    method: 'PATCH',
    path: `/v1/delivered-sessions/${id}/payment`,
    body,
  });

/**
 * Correct the catalogue-defaulted amount of an EFFECTIVE OVER-PLAN delivery
 * (clinic.sessions.payment.record). Anything else answers
 * 409 SESSION_PAYABLE_AMOUNT_LOCKED. Ledgered in the patient change history.
 */
export const overridePayableAmount = (
  id: string,
  body: { payableAmount: number; reason?: string },
) =>
  apiRequest<DeliveredSessionView>({
    base: 'clinic',
    method: 'PATCH',
    path: `/v1/delivered-sessions/${id}/payable-amount`,
    body,
  });

/** The backend's own definition, for rows read before `overPlan` was derived on the wire. */
export const isOverPlan = (
  s: Pick<DeliveredSessionView, 'packageItemId' | 'priceBasis' | 'overPlan'>,
): boolean => s.overPlan ?? (s.packageItemId !== null && s.priceBasis === 'catalog');
